import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useWizard } from '@leverege/ui-wizard';
import I18N from '@leverege/i18n';
import { Flex, Pane, Text, ToggleButton } from '@leverege/ui-elements';
import { DeviceSelector, ItemDelegate } from '@leverege/molten/imagine';
import { useSelector } from 'react-redux';
import { GlobalState } from '@leverege/ui-redux';
import { ItemCard } from '@leverege/ui-attributes';
import Util from "./Util.js";
const {
  t,
  tIcon
} = I18N.ns('pairing.imagine.UnpairDevice');
export default function SelectUnpairDevice(props) {
  const {
    context,
    setHeader,
    setHeaderIcon,
    setNextDisabled,
    setNextText,
    onChange,
    wizardData,
    wizardId
  } = useWizard();
  const {
    assetDelegate,
    attribute,
    parentAttribute,
    parentRelationship,
    relationship,
    tagDelegate
  } = context;
  const assetName = relationship.parentBlueprint.name;
  const tagName = relationship.blueprint.name;
  const nAssetData = wizardData?.asset;
  const nTagData = wizardData?.tag;
  const [deviceType, setDeviceType] = useState('asset');
  const asset = useSelector(state => {
    let item;
    if (wizardData?.asset?.obj) {
      item = wizardData.asset.obj.id;
    }
    if (!item) {
      return null;
    }
    return ItemDelegate.getItem(item, state);
  });
  const tag = useSelector(state => {
    let item;
    if (wizardData?.tag?.obj) {
      item = wizardData.tag.obj.id;
    }
    if (!item) {
      return null;
    }
    return ItemDelegate.getItem(item, state);
  });
  const AssetSelectionRenderer = useMemo(() => {
    return Util.getSelectionRenderer(parentRelationship.path, parentRelationship.objectType, 'unpair');
  }, [parentRelationship]);
  const TagSelectionRenderer = useMemo(() => {
    return Util.getSelectionRenderer(parentRelationship.path, relationship.objectType, 'unpair');
  }, [parentRelationship, relationship]);
  const onChangeAsset = useCallback(({
    data,
    value: deviceId
  }) => {
    if (deviceId == null) {
      onChange({
        data,
        value: deviceId
      });
      return;
    }
    const change = {
      data: {},
      value: {}
    };
    const assetObj = {
      id: deviceId,
      type: parentRelationship.objectType,
      ref: assetDelegate.getInterfaceUrl(),
      data: ItemDelegate.getItem(deviceId)
    };
    change.data['asset/obj'] = 'asset';
    change.value.asset = assetObj;
    const tagId = attribute.get(assetObj.data);
    if (tagId) {
      const assetObj = {
        id: tagId,
        type: relationship.objectType,
        ref: tagDelegate.getInterfaceUrl(),
        data: ItemDelegate.getItem(tagId)
      };
      change.data['tag/obj'] = 'tag';
      change.value.tag = assetObj;
      if (!ItemDelegate.getItem(tagId)) {
        GlobalState.dispatch(tagDelegate.item(tagId).get());
      }
    }
    onChange(change);
  }, []);
  const onChangeTag = useCallback(({
    data,
    value: deviceId
  }) => {
    if (deviceId == null) {
      onChange({
        data,
        value: deviceId
      });
      return;
    }
    const change = {
      data: {},
      value: {}
    };
    const val = {
      id: deviceId,
      type: relationship.objectType,
      ref: tagDelegate.getInterfaceUrl(),
      data: ItemDelegate.getItem(deviceId)
    };
    change.data['tag/obj'] = 'tag';
    change.value.tag = val;
    const assetId = parentAttribute.get(val.data);
    if (assetId) {
      const assetObj = {
        id: assetId,
        type: parentRelationship.objectType,
        ref: assetDelegate.getInterfaceUrl(),
        data: ItemDelegate.getItem(assetId)
      };
      change.data['asset/obj'] = 'asset';
      change.value.asset = assetObj;
    }
    onChange(change);
  }, []);
  const onChangeDeviceType = useCallback(({
    data
  }) => {
    setDeviceType(data);
    onChange({
      data: {
        'asset/obj': 'asset',
        'tag/obj': 'tag'
      },
      value: {
        asset: null,
        tag: null
      }
    });
  }, [onChange]);
  useEffect(() => {
    setHeader(t('header', {
      assetName,
      tagName
    }));
    setHeaderIcon(tIcon('headerIcon'));
    setNextText(t('unpair'));
  }, []);
  useEffect(() => {
    const pairingMeta = attribute?.source?.attribute?.metadata?.pairing?.unpair;
    if (!asset && !tag) {
      setNextDisabled(true);
    } else if (pairingMeta?.asset?.extraFields || pairingMeta?.tag?.extraFields) {
      const assetAttributes = Util.getEditableAttributes(pairingMeta?.asset, parentRelationship?.objectType, {
        requiredOnly: true
      });
      const tagAttributes = Util.getEditableAttributes(pairingMeta?.tag, relationship?.objectType, {
        requiredOnly: true
      });
      let disabled = false;
      if (assetAttributes) {
        disabled = assetAttributes.some(attr => {
          const val = attr.get(wizardData.asset);
          return val == null;
        });
      }
      if (tagAttributes) {
        disabled = disabled || tagAttributes.some(attr => {
          const val = attr.get(wizardData.tag);
          return val == null;
        });
      }
      setNextDisabled(disabled);
    } else {
      setNextDisabled(false);
    }
  }, [asset, attribute, parentRelationship, relationship, setNextDisabled, tag, wizardData]);
  const groupDelegate = useMemo(() => {
    if (deviceType === 'asset') {
      const delegate = Util.getPairingDelegate(parentRelationship.path, parentRelationship.objectType, 'unpair', context);
      return delegate ?? assetDelegate;
    }
    const delegate = Util.getPairingDelegate(parentRelationship.path, relationship.objectType, 'unpair', context);
    return delegate ?? tagDelegate;
  }, [context, assetDelegate, tagDelegate, relationship, parentRelationship, deviceType]);
  const {
    relationshipEditor,
    extraAssetEditors,
    extraTagEditors
  } = useMemo(() => {
    let relationshipEditor;
    const pairingMeta = attribute?.source?.attribute?.metadata?.pairing?.unpair;
    if (deviceType === 'asset') {
      const deviceSources = pairingMeta?.asset?.deviceSources;
      let searchFields = pairingMeta?.asset?.searchFields;
      searchFields = searchFields && searchFields.length > 0 ? Util.getSearchFieldsFromAttributes(searchFields, parentRelationship.objectType) : null;
      const getFilters = () => {
        return [{
          type: 'exists',
          field: attribute.source?.field
        }];
      };
      relationshipEditor = /*#__PURE__*/React.createElement(DeviceSelector, {
        key: nAssetData?.obj?.id || 'asset',
        targetId: wizardId,
        usage: "pairing",
        value: nAssetData?.obj?.id,
        eventData: "asset/obj",
        onChange: onChangeAsset,
        delegate: groupDelegate,
        relationshipPaths: deviceSources,
        itemRenderer: AssetSelectionRenderer,
        getFilters: getFilters,
        searchFields: searchFields,
        searchHeaderTitle: t('searchHeaderTitle', {
          blueprintName: assetName
        })
      });
    } else {
      const deviceSources = pairingMeta?.tag?.deviceSources;
      let searchFields = pairingMeta?.tag?.searchFields;
      searchFields = searchFields && searchFields.length > 0 ? Util.getSearchFieldsFromAttributes(searchFields, relationship.objectType) : null;
      const getFilters = () => {
        return [{
          type: 'exists',
          field: parentAttribute.source?.field
        }];
      };
      relationshipEditor = /*#__PURE__*/React.createElement(DeviceSelector, {
        key: nTagData?.obj?.id || 'tag',
        targetId: wizardId,
        usage: "pairing",
        value: nTagData?.obj?.id,
        eventData: "tag/obj",
        onChange: onChangeTag,
        delegate: groupDelegate,
        relationshipPaths: deviceSources,
        itemRenderer: TagSelectionRenderer,
        getFilters: getFilters,
        searchFields: searchFields,
        searchHeaderTitle: t('searchHeaderTitle', {
          blueprintName: tagName
        })
      });
    }
    const extraAssetEditors = Util.getAttributeEditors(pairingMeta?.asset, {
      relationship: parentRelationship,
      deviceFormData: nAssetData,
      device: asset,
      disabled: !asset && !tag,
      onChange,
      delegate: groupDelegate,
      type: 'asset'
    });
    const extraTagEditors = Util.getAttributeEditors(pairingMeta?.tag, {
      relationship,
      deviceFormData: nTagData,
      device: tag,
      disabled: !asset && !tag,
      onChange,
      delegate: groupDelegate,
      type: 'tag'
    });
    return {
      relationshipEditor,
      extraAssetEditors,
      extraTagEditors
    };
  }, [asset, attribute, deviceType, groupDelegate, nAssetData, nTagData, onChange, onChangeAsset, onChangeTag, parentRelationship, relationship, tag, wizardId]);
  const assetRenderers = useMemo(() => {
    if (!wizardData?.asset?.obj || !asset) {
      return null;
    }
    const pairingMeta = attribute?.source?.attribute?.metadata?.pairing?.unpair?.asset;
    let extraFields = pairingMeta?.extraFields;
    extraFields = extraFields || [{
      name: 'name'
    }];
    const model = Util.getItemModelFromAttributes(extraFields, {
      objectType: parentRelationship.objectType
    });
    if (!model) {
      return null;
    }
    const target = [{
      ...wizardData.asset.obj,
      data: {
        ...asset
      }
    }];
    return /*#__PURE__*/React.createElement(ItemCard, {
      model: model,
      context: {
        target
      }
    });
  }, [asset, attribute, parentRelationship, wizardData]);
  const tagRenderers = useMemo(() => {
    if (!wizardData?.tag?.obj || !tag) {
      return null;
    }
    const pairingMeta = attribute?.source?.attribute?.metadata?.pairing?.unpair?.tag;
    let extraFields = pairingMeta?.extraFields;
    extraFields = extraFields || [{
      name: 'name'
    }];
    const model = Util.getItemModelFromAttributes(extraFields, {
      objectType: relationship.objectType
    });
    if (!model) {
      return null;
    }
    const target = [{
      ...wizardData.tag.obj,
      data: {
        ...tag
      }
    }];
    return /*#__PURE__*/React.createElement(ItemCard, {
      model: model,
      context: {
        target
      }
    });
  }, [tag, attribute, relationship, wizardData]);
  return /*#__PURE__*/React.createElement(Flex, {
    variant: "colL"
  }, /*#__PURE__*/React.createElement(Text, {
    variant: "formTitle"
  }, t('title', {
    assetName,
    tagName
  })), /*#__PURE__*/React.createElement(Text, {
    variant: "bodySmall"
  }, t('description', {
    assetName,
    tagName
  })), /*#__PURE__*/React.createElement(Flex, {
    variant: "rowM"
  }, /*#__PURE__*/React.createElement(ToggleButton, {
    value: deviceType === 'asset',
    variant: "pairingSelector",
    eventData: "asset",
    onChange: onChangeDeviceType
  }, assetName), /*#__PURE__*/React.createElement(ToggleButton, {
    value: deviceType === 'tag',
    variant: "pairingSelector",
    eventData: "tag",
    onChange: onChangeDeviceType
  }, tagName)), relationshipEditor, extraAssetEditors, extraTagEditors, tagRenderers && /*#__PURE__*/React.createElement("hr", {
    style: {
      width: '100%',
      height: 1,
      borderColor: 'var(--color-base200)',
      borderStyle: 'solid',
      borderBottom: 'none'
    }
  }), tagRenderers && /*#__PURE__*/React.createElement(Pane, {
    layout: "flex:colS",
    style: {
      '--color-detailEvenRow': 'transparent',
      '--color-detailOddRow': 'rgba(0, 0, 0, 0.03)'
    }
  }, /*#__PURE__*/React.createElement(Text, {
    variant: "detailValue"
  }, t('tagDetails', {
    tagName
  })), /*#__PURE__*/React.createElement(Pane, {
    variant: "detailCardNoShadow"
  }, tagRenderers)), assetRenderers && /*#__PURE__*/React.createElement(Pane, {
    layout: "flex:colS",
    style: {
      marginTop: 10,
      '--color-detailEvenRow': 'transparent',
      '--color-detailOddRow': 'rgba(0, 0, 0, 0.03)'
    }
  }, /*#__PURE__*/React.createElement(Text, {
    variant: "detailValue"
  }, t('assetDetails', {
    assetName
  })), /*#__PURE__*/React.createElement(Pane, {
    variant: "detailCardNoShadow"
  }, assetRenderers)));
}
SelectUnpairDevice.TYPE = 'imagine.pairing.SelectUnpairDevice';